import { usePushPromptStore } from '@/store/usePushPromptStore'
import { useIsNativeUpdateShowing } from '@/lib/hooks/useNativeUpdateDecision'

/** Everything that wants the bottom corner, most important first. */
export const BOTTOM_SURFACE_PRIORITY = ['native-update', 'push-prompt', 'slow-connection'] as const

export type BottomSurfaceClaimant = (typeof BOTTOM_SURFACE_PRIORITY)[number]

export type BottomSurfaceSignals = {
  /** The native update is on screen in one of its forms (card, interstitial, blocking). */
  'native-update': boolean
  /** The push-permission card's own gate says it may show. */
  'push-prompt': boolean
}

/**
 * For each claimant, the ones that outrank it — the ladder, spelled out.
 *
 * Written as data rather than derived from an index into
 * `BOTTOM_SURFACE_PRIORITY` so that the relation a caller depends on is
 * readable at the point of use; a test pins the two against each other so they
 * cannot drift apart.
 *
 * `slow-connection` needs no entry of its own as a KEY of the signals, because
 * nothing ranks below it and so nobody ever asks about it.
 */
const OUTRANKED_BY: Record<BottomSurfaceClaimant, readonly (keyof BottomSurfaceSignals)[]> = {
  'native-update': [],
  'push-prompt': ['native-update'],
  'slow-connection': ['native-update', 'push-prompt'],
}

/**
 * Which single surface owns the bottom corner, if any.
 *
 * Bottom-anchored cards used to claim that corner with no arbitration at all,
 * and they were measured overlapping on 2026-08-26. The first fix gave the
 * slow-connection prompt a boolean "is anyone else there?" to yield on, which
 * closed that instance and left the CLASS open — the very next pair collided
 * the same way: `PushPromptCard` sits 84px above the tab bar at z-95 and
 * `NativeVersionUpdateCard` 12px above it at z-120, both `inset-x-4`, and with
 * card heights around 130px the update card covered the push card's entire
 * Enable/Later/Never row. The user could see the offer and not press it.
 *
 * So the boolean became an ordered ladder. There is exactly one owner, the
 * order is data (`BOTTOM_SURFACE_PRIORITY`), and every claimant asks the same
 * question, which is what stops a third card from arriving with a third
 * private opinion about who yields to whom.
 *
 * The order is by how much the surface has earned the interruption:
 * a required app update outranks an onboarding nudge, which outranks an
 * observation about connection quality. Nothing here is an error or a task the
 * user started; those are dialogs and toasts, and they live above all of it.
 *
 * Yielding is wider than geometry strictly requires — on desktop the push card
 * is bottom-LEFT while the others are bottom-right, so they do not overlap
 * there. That is deliberate: two suggestion cards up at once is the pushiness
 * this change exists to remove.
 *
 * `BulkActionBar` is NOT a claimant, though it reads like one. Its only
 * production mount is `FiltersDesktop.tsx:51` with `inline`, which renders in
 * normal flow in the header row; the floating bottom variant has no caller.
 * Treating selection mode as an occupied corner would hide a card while
 * nothing was there — a suppression with no visible cause.
 */
export function isBottomSurfaceClaimedAbove(
  claimant: BottomSurfaceClaimant,
  signals: BottomSurfaceSignals,
): boolean {
  return OUTRANKED_BY[claimant].some(other => signals[other])
}

/**
 * Whether anything ABOVE `claimant` currently owns the bottom corner.
 *
 * A surface asks only about its superiors, never about itself or its
 * inferiors. That asymmetry is the point: it is what lets each card call this
 * without any card needing to read another card's local state. An earlier
 * draft had this hook read every claimant's gate and return a single winner,
 * which forced it to call `useSlowConnectionDetector` — a hook holding local
 * state — a SECOND time. Two instances mean two `snoozedUntil` values, so the
 * user's "not now" would have updated one of them and left the other still
 * showing the prompt.
 */
export function useIsBottomSurfaceClaimedAbove(claimant: BottomSurfaceClaimant): boolean {
  const nativeUpdateShowing = useIsNativeUpdateShowing()
  const pushPromptWants = usePushPromptStore(state => state.isVisible)

  return isBottomSurfaceClaimedAbove(claimant, {
    'native-update': nativeUpdateShowing,
    'push-prompt': pushPromptWants,
  })
}
